import React, { useState, useEffect } from "react";
import styles from "./HeroSection.module.css";
import { Container } from "../../common/Container";
import { heroData } from "../../../data/tidePostData";

const AppStoreIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={styles.appStoreIcon}>
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
  </svg>
);

const CheckIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
  </svg>
);

const WaveIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M2.25 12c0-1.5 1.5-3 3-3s3 1.5 3 3-1.5 3-3 3-3-1.5-3-3zm6 0c0-1.5 1.5-3 3-3s3 1.5 3 3-1.5 3-3 3-3-1.5-3-3zm6 0c0-1.5 1.5-3 3-3s3 1.5 3 3-1.5 3-3 3-3-1.5-3-3z" />
    <path
      fillRule="evenodd"
      d="M1.5 12.75a.75.75 0 010-1.5h.75a.75.75 0 010 1.5H1.5zm19.5 0a.75.75 0 010-1.5h.75a.75.75 0 010 1.5H21zM12 2.25a.75.75 0 01.75.75v1.5a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM12 18a.75.75 0 01.75.75v1.5a.75.75 0 01-1.5 0v-1.5A.75.75 0 0112 18z"
      clipRule="evenodd"
    />
  </svg>
);

const NOAAIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-.778.099-1.533.284-2.253" />
  </svg>
);

const screenshots = [
  {
    src: "/images/tide-post/screenshot-tides.png",
    alt: "Tide Post tides view showing tide chart, countdown, water level, and moon phase",
    label: "Tides",
  },
  {
    src: "/images/tide-post/screenshot-map.png",
    alt: "Tide Post stations view showing the NOAA map with station markers",
    label: "Map",
  },
  {
    src: "/images/tide-post/screenshot-details.png",
    alt: "Tide Post station details view with tide information",
    label: "Details",
  },
];

export const HeroSection: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  // Bumped on every tab click so re-clicking the active tab also restarts the timer.
  const [clickCount, setClickCount] = useState(0);

  // Restart the full countdown whenever the slide changes or a tab is clicked.
  useEffect(() => {
    const timeout = setTimeout(() => {
      setActiveIndex((prev) => (prev + 1) % screenshots.length);
    }, 4000);
    return () => clearTimeout(timeout);
  }, [activeIndex, clickCount]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section className={styles.hero}>
      <div className={styles.backgroundGlow} />
      <Container>
        <div className={styles.content}>
          <div className={styles.textContent}>
            <div className={styles.badge}>
              <span className={styles.badgeIcon}>
                <WaveIcon />
              </span>
              <span>Now available on iOS</span>
            </div>

            <h1 className={styles.headline}>{heroData.headline}</h1>
            <p className={styles.subheadline}>{heroData.subheadline}</p>

            <div className={styles.features}>
              <div className={styles.featureItem}>
                <CheckIcon />
                <span>Free to use</span>
              </div>
              <div className={styles.featureItem}>
                <CheckIcon />
                <span>No account required</span>
              </div>
              <div className={styles.featureItem}>
                <CheckIcon />
                <span>NOAA data</span>
              </div>
            </div>

            <div className={styles.ctas}>
              <a
                href={heroData.appStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.primaryCta}
              >
                <AppStoreIcon />
                <div className={styles.ctaText}>
                  <span className={styles.ctaSmall}>Download on the</span>
                  <span className={styles.ctaLarge}>App Store</span>
                </div>
              </a>
              <button
                onClick={() => scrollToSection("features")}
                className={styles.secondaryCta}
              >
                {heroData.ctaSecondary}
              </button>
            </div>

            <div className={styles.stats}>
              <NOAAIcon />
              <span>Powered by NOAA Tides & Currents</span>
            </div>
          </div>

          <div className={styles.mockupContainer}>
            <div className={styles.mockupGlow} />
            <div className={styles.phoneMockup}>
              <div className={styles.phoneFrame}>
                <div className={styles.phoneScreen}>
                  {screenshots.map((screenshot, index) => (
                    <img
                      key={screenshot.src}
                      src={screenshot.src}
                      alt={screenshot.alt}
                      className={`${styles.screenshot} ${
                        index === activeIndex ? styles.screenshotActive : ""
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className={styles.screenshotNav}>
              {screenshots.map((screenshot, index) => (
                <button
                  key={screenshot.label}
                  className={`${styles.screenshotDot} ${
                    index === activeIndex ? styles.screenshotDotActive : ""
                  }`}
                  onClick={() => {
                    setActiveIndex(index);
                    setClickCount((c) => c + 1);
                  }}
                  aria-label={`View ${screenshot.label} screenshot`}
                >
                  <span className={styles.dotLabel}>{screenshot.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};
